import React, { useState, useEffect, useRef } from 'react';
import { MarkdownService } from '../../lib/services/markdownService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import './MarkdownPreview.css';

export const MarkdownPreview: React.FC = () => {
  const [input, setInput] = useState('');
  const [htmlOutput, setHtmlOutput] = useState('');
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleConvert = async () => {
    if (!input.trim()) {
      setHtmlOutput('');
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = input.substring(0, 50);
    processingRef.current = processingKey;

    try {
      const result = await MarkdownService.toHtml(input);

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        setHtmlOutput(result.outputData as string);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, 'markdown-preview' as any);
        setError(errorMessage);
        setHtmlOutput('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleConvert();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setHtmlOutput('');
      setError(null);
      setIsLoading(false);
    }
  }, [input]);

  const handleCopy = async () => {
    if (!htmlOutput) return;

    try {
      const success = await ClipboardUtils.copyText(htmlOutput);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy HTML';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!htmlOutput) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="markdown-preview">
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>Markdown preview requires JavaScript. Please enable JavaScript to use this tool.</p>
        </div>
      </noscript>

      <div style={{ marginBottom: '2rem' }}>
        <h1>Markdown Preview</h1>
        <p>
          Write Markdown and see the live HTML preview. Supports basic Markdown syntax
          including headings, lists, links, images, code blocks, and more.
        </p>
      </div>

      <div className="markdown-container">
        <div className="split-pane">
          <div className="pane editor-pane">
            <div className="pane-header">
              <label>Markdown Input:</label>
            </div>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="# Enter Markdown here..."
              data-testid="input-text"
              className="markdown-editor"
            />
          </div>

          <div className="pane preview-pane">
            <div className="pane-header">
              <label>Live Preview:</label>
            </div>
            <div
              className="markdown-output"
              dangerouslySetInnerHTML={{ __html: htmlOutput || '<p class="placeholder">Preview will appear here...</p>' }}
            />
            {isLoading && (
              <div className="loading-indicator">Processing...</div>
            )}
          </div>
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        <div className="button-container">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!htmlOutput}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
